import { Breadcrumbs, Loader, Skeleton, Tabs } from '@mantine/core';
import { IconCubePlus, IconCurrencyBaht, IconRefresh } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AccessBadge, Button } from '../../../../components/Atoms';
import { useBaremetalListQuery } from '../../../../hooks/CloudComputeHooks/useBaremetalListQuery';
import { useProductAccess } from '../../../../hooks/useActiveProject';
import useBreadcrumbItems from '../../../../hooks/useBreadcrumbItems';
import usePageHeading from '../../../../hooks/usePageHeading';
import BaremetalListTab from './components/BaremetalListTab/BaremetalListTab';

export default function BareMetalComputePage() {
    const { t } = useTranslation('bareMetal');
    const headingRef = usePageHeading(t('page.title'));
    const [isRefreshing, setIsRefreshing] = useState(false);
    const {
        data: baremetals,
        isPending,
        collective,
        refetch,
    } = useBaremetalListQuery({ refetchInterval: 3500 });
    const baremetalAccess = useProductAccess('baremetal');

    const breadcrumbItems = useBreadcrumbItems([
        { title: t('breadcrumb.product'), href: '#' },
        { title: t('breadcrumb.bareMetal'), href: '/bare-metal-compute' },
    ]);

    return (
        <div className="w-full min-h-full flex flex-col justify-start items-center gap-8">
            <div className="w-full flex flex-col justify-start items-start">
                <Breadcrumbs separatorMargin={'xs'} className="mb-6">
                    {breadcrumbItems}
                </Breadcrumbs>
                <div className="w-full flex flex-col lg:flex-row gap-2 justify-between lg:gap-0">
                    <div className="flex flex-col justify-start items-start gap-1">
                        <div className="flex flex-row flex-wrap items-center gap-2">
                            <h1 ref={headingRef} tabIndex={-1} className="text-3xl font-bold">
                                {t('page.title')}
                            </h1>
                            {baremetalAccess && baremetalAccess !== 'manage' && (
                                <AccessBadge level={baremetalAccess} />
                            )}
                        </div>
                        <p className="w-auto max-w-135 text-base text-fg/68">
                            {t('page.description')}
                        </p>
                    </div>
                    <div className="flex flex-row flex-wrap justify-start lg:justify-end items-end gap-2">
                        <Button
                            size="sm"
                            variant="secondary"
                            leftIcon={<IconRefresh stroke={1.5} />}
                            onClick={() => {
                                setIsRefreshing(true);
                                void refetch().finally(() => {
                                    setIsRefreshing(false);
                                });
                            }}
                            isLoading={isRefreshing}
                        >
                            {t('buttons.refresh')}
                        </Button>
                        {baremetalAccess === 'manage' && (
                            <Button
                                href="/bare-metal-compute/deployment"
                                size="sm"
                                leftIcon={<IconCubePlus stroke={1.5} aria-hidden="true" />}
                                className="w-full lg:w-auto"
                            >
                                {t('buttons.deployment')}
                            </Button>
                        )}
                    </div>
                </div>
            </div>
            <div className="w-full grid grid-cols-1 lg:grid-cols-4 border-[0.5px] border-line rounded-sm">
                <div className="w-full h-auto p-4 flex flex-col justify-start items-start gap-1 border-b-[0.5px] border-line lg:border-b-0 bg-surface-1 rounded-none lg:rounded-l-sm">
                    <h2 className="text-xs text-fg/68 md:text-base">{t('stats.activeServers')}</h2>
                    {collective && !isPending ? (
                        <div className="inline-flex items-center gap-1">
                            <span className="text-xl font-bold md:text-2xl">
                                {collective.servers}
                            </span>
                            <span className="text-xs text-fg/68 md:text-base">
                                {t('stats.serversSuffix')}
                            </span>
                        </div>
                    ) : (
                        <Skeleton height={31} width={'100%'} radius="sm" />
                    )}
                </div>
                <div className="w-full h-auto p-4 flex flex-col justify-start items-start gap-1 bg-surface-1 border-b-[0.5px] border-l-0 lg:border-l-[0.5px] lg:border-b-0 border-line">
                    <h2 className="text-xs text-fg/68 md:text-base">{t('stats.totalMonthly')}</h2>
                    {collective && !isPending ? (
                        <div className="inline-flex items-center gap-1">
                            <div className="inline-flex items-center text-xl font-bold md:text-2xl">
                                {collective.monthly.toFixed(2)}
                                <IconCurrencyBaht stroke={2.5} />
                            </div>
                            <span className="text-xs text-fg/68 text-nowrap md:text-base">
                                {t('stats.monthlySuffix')}
                            </span>
                        </div>
                    ) : (
                        <Skeleton height={31} width={'100%'} radius="sm" />
                    )}
                </div>
                <div className="w-full h-auto p-4 flex flex-col justify-start items-start gap-1 bg-surface-1 border-b-[0.5px] border-l-0 lg:border-l-[0.5px] lg:border-b-0 border-line">
                    <h2 className="text-xs text-fg/68 md:text-base">{t('stats.autoRenewOn')}</h2>
                    {collective && !isPending ? (
                        <div className="inline-flex items-center gap-1">
                            <span className="text-xl font-bold md:text-2xl">
                                {collective.autoRenewOn}
                            </span>
                            <span className="text-xs text-fg/68 md:text-base">
                                {t('stats.serversSuffix')}
                            </span>
                        </div>
                    ) : (
                        <Skeleton height={31} width={'100%'} radius="sm" />
                    )}
                </div>
                <div className="w-full h-auto p-4 flex flex-col justify-start items-start gap-1 bg-surface-1 border-l-0 lg:border-l-[0.5px] border-line rounded-r-sm">
                    <h2 className="text-xs text-fg/68 md:text-base">{t('stats.autoRenewOff')}</h2>
                    {collective && !isPending ? (
                        <div className="inline-flex items-center gap-1">
                            <span className="text-xl font-bold md:text-2xl">
                                {collective.autoRenewOff}
                            </span>
                            <span className="text-xs text-fg/68 md:text-base">
                                {t('stats.serversSuffix')}
                            </span>
                        </div>
                    ) : (
                        <Skeleton height={31} width={'100%'} radius="sm" />
                    )}
                </div>
            </div>
            <Tabs
                className="w-full space-y-4"
                defaultValue={'baremetal'}
                color="var(--fg)"
                keepMounted={false}
            >
                <Tabs.List aria-label={t('tabs.label')} className="tabs-list-thin">
                    <Tabs.Tab
                        value={'baremetal'}
                        rightSection={
                            <div className="w-5 h-5 flex justify-center items-center font-bold text-xs rounded-sm bg-surface-3">
                                {collective && !isPending ? (
                                    collective.servers
                                ) : (
                                    <Loader size={8} color="var(--fg)" type="bars" />
                                )}
                            </div>
                        }
                    >
                        {t('tabs.baremetal')}
                    </Tabs.Tab>
                </Tabs.List>

                <BaremetalListTab baremetals={baremetals} isLoading={isPending} />
            </Tabs>
        </div>
    );
}
